import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CheckCircle2, Sparkles, UserSearch, Briefcase, Cloud, Layers, Cpu, Building2 } from 'lucide-react';
import Reveal from '../components/Reveal';
import SectionHeader from '../components/SectionHeader';
import { CORE_SERVICES, IMAGES } from '../mock/data';

const IconMap = { UserSearch, Briefcase, Cloud, Layers, Cpu, Building2 };

const SERVICE_DETAILS = [
  {
    icon: 'UserSearch',
    title: 'IT Talent Acquisition & Recruitment',
    desc: 'Vetted software, cloud, AI/ML and data engineers matched to your exact stack — contract, C2H and direct hire.',
    bullets: ['Senior AI, ML, data and MLOps engineers, pre-vetted', 'Technical screening & reference checks before you interview', 'Onboarding support and ongoing consultant care', 'Compliance, payroll and W-2 employment handled'],
  },
  {
    icon: 'Cpu',
    title: 'Technology & IT Consulting',
    desc: 'From architecture to DevOps to cloud engineering — advisory that turns your roadmap into shipped results.',
    bullets: ['Full-Stack Development (Node.js, React)', 'CI/CD & DevOps engineering', 'Cloud Engineering (AWS, Azure, GCP)', 'Cybersecurity, AI/ML models and IoT implementation'],
  },
  {
    icon: 'Cloud',
    title: 'SaaS Implementation & Support',
    desc: 'End-to-end configuration, integration and support of enterprise SaaS platforms — done right, first time.',
    bullets: ['Platform selection & requirement mapping', 'Custom integrations and data migration', 'User onboarding and training', 'Ongoing support and optimisation'],
  },
  {
    icon: 'Layers',
    title: 'Workforce & Staffing Solutions',
    desc: 'Scalable workforce solutions across tech, finance and operations — flexible engagement models built for growth.',
    bullets: ['Contract, C2H and direct hire', 'Executive search for leadership roles', 'Managed service teams', 'Compliance & payroll included'],
  },
  {
    icon: 'Briefcase',
    title: 'Administrative, Finance & Accounting Staffing',
    desc: 'Beyond tech — we source specialised back-office talent that keeps your operations moving.',
    bullets: ['Finance & accounting professionals', 'HR and operations specialists', 'Executive assistants and admin support', 'Long-term and project-based engagements'],
  },
  {
    icon: 'Building2',
    title: 'BPO & KPO Services',
    desc: 'Business and knowledge process outsourcing with a US-based delivery hub and global talent bench.',
    bullets: ['Customer support & back-office operations', 'Research, analytics and knowledge processes', 'Dedicated pods with SLAs', '24/7 coverage available'],
  },
];

const Services = () => (
  <>
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="hero-glow" />
      <div className="container-x relative">
        <Reveal>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-medium uppercase tracking-widest mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Our Services
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white leading-[1.05] max-w-4xl">
            End-to-end <span className="shimmer-text">Talent, Tech & SaaS</span> solutions.
          </h1>
        </Reveal>
        <Reveal delay={160}>
          <p className="text-white/60 text-lg max-w-2xl mt-6">
            Expert staffing solutions tailored to meet your business needs and drive success — paired with in-house engineering capability across the full modern tech stack.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="section">
      <div className="container-x">
        <div className="space-y-8">
          {SERVICE_DETAILS.map((s, i) => {
            const Ico = IconMap[s.icon];
            const reverse = i % 2 === 1;
            return (
              <Reveal key={i} delay={i * 60}>
                <div className={`glass rounded-2xl border border-white/10 overflow-hidden grid lg:grid-cols-5 gap-0`}>
                  <div className={`lg:col-span-3 p-8 md:p-12 ${reverse ? 'lg:order-2' : ''}`}>
                    <div className="w-12 h-12 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center text-cyan-300 mb-5">
                      <Ico className="w-6 h-6" />
                    </div>
                    <div className="text-xs uppercase tracking-widest text-cyan-300/80 mb-2">Service 0{i + 1}</div>
                    <h3 className="font-display text-2xl md:text-3xl font-semibold text-white leading-tight">{s.title}</h3>
                    <p className="text-white/60 mt-3 leading-relaxed">{s.desc}</p>
                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 mt-6">
                      {s.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-2.5 text-sm text-white/80">
                          <CheckCircle2 className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" /> {b}
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact" className="btn-outline inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm mt-7">
                      Talk to an expert <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                  <div className={`lg:col-span-2 relative min-h-[240px] ${reverse ? 'lg:order-1' : ''}`}>
                    <img src={[IMAGES.hiringA, IMAGES.netA, IMAGES.aboutA, IMAGES.hiringB, IMAGES.netB, IMAGES.aboutB][i % 6]} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#05070E]/40 via-transparent to-[#05070E]/20" />
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  </>
);

export default Services;
